import { t } from "i18next";

import { IStationDto, IStationInfo } from "../dto";
import { DirectionEnum } from "../enums";
import { Api, getCompanyId, Log } from "../helpers";
import {
  ICreateStation,
  IDropdownItemStations,
  IHubWithStationPairs,
  INearbyStationDto,
  IStationPossiblePassengers,
} from "../models";

export class StationApi {
  static async searchNearbyStation({
    stationId,
    passengerId,
    distanceInMeters,
  }: {
    stationId: number;
    passengerId?: number;
    distanceInMeters?: number;
  }): Promise<INearbyStationDto | undefined> {
    const companyId = getCompanyId();

    let requestUrl = `/api/v1/core/c/${companyId}/stations/${stationId}/nearby`;

    const queryParams: string[] = [];
    if (passengerId) {
      queryParams.push(`passengerId=${passengerId}`);
    }
    if (distanceInMeters) {
      queryParams.push(`distanceInMeters=${distanceInMeters}`);
    }

    if (queryParams?.length > 0) {
      requestUrl += `?${queryParams.join("&")}`;
    }

    const response = await Api.get<INearbyStationDto | undefined>(requestUrl);
    return response?.data;
  }

  static async fetchStationInfo(
    stationId: number,
  ): Promise<IStationInfo[] | undefined> {
    if (!stationId) {
      Log.warnAndToast(t("errors.invalidIdPassStationInfo"));
      return [];
    }

    const response = await Api.post<IStationInfo[]>(
      `/api/v1/core/c/${getCompanyId()}/stations/station-infos`,
      { ids: [stationId] },
    );

    return response?.data || [];
  }

  static async fetchPotentialPassengersInfo(
    stationIds: number[],
  ): Promise<IStationPossiblePassengers[] | undefined> {
    if (!stationIds.length) {
      Log.warnAndToast(t("errors.invalidIdPassStationInfo"));
      return [];
    }

    const response = await Api.post<IStationPossiblePassengers[]>(
      `/api/v1/core/c/${getCompanyId()}/stations/stations-potential-passengers`,
      { stationIds },
    );

    return response?.data || [];
  }

  static async createStation(
    data: ICreateStation,
  ): Promise<IStationDto | undefined> {
    const response = await Api.post<IStationDto>(
      `/api/v1/core/c/${getCompanyId()}/stations`,
      data,
    );

    return response?.data;
  }

  static async getHubStationsForPair(
    originStationId: number,
    destinationStationId: number,
    currentHubId?: number,
    direction?: DirectionEnum,
  ): Promise<IDropdownItemStations[]> {
    const response = await Api.post<IDropdownItemStations[]>(
      `/api/v1/core/c/${getCompanyId()}/stations/hub-stations-for-pair`,
      { originStationId, destinationStationId, currentHubId, direction },
    );
    return response?.data ?? [];
  }

  static async getHubsWithStationPairs(): Promise<IHubWithStationPairs[]> {
    const response = await Api.get<IHubWithStationPairs[]>(
      `/api/v1/core/c/${getCompanyId()}/stations/hubs-with-station-pairs`,
    );
    return response?.data ?? [];
  }
}
